/* eslint-disable no-unused-vars */
import { Badge, Dropdown, Spin } from "antd";
import { Button } from "antd/es";
import axios from "axios";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { FiActivity, FiAlertTriangle, FiCheckCircle, FiInfo, FiMaximize } from "react-icons/fi";
import { IoMdNotificationsOutline } from "react-icons/io";
import { IoMoon, IoSunnySharp } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { api, endpoint } from "@/api";
import { socket } from "@/api/socket";
import { userRole, userRoleFormate } from "@/constant/userRole";
import { changeNavOpen, changeTheme } from "@/redux/slices/theme.slice";
import { setActiveDashboard, setIsLogged, setUserDetails } from "@/redux/slices/user.slice";
import HomeSelect from "./HomeSelect";
import { flare } from "@flareapp/js";

const getNotificationStatus = (status) => {
  const s = status?.toLowerCase();
  if (["critical", "warning", "info", "success"].includes(s)) {
    return s;
  }
  return "info";
};

const getStatusIcon = (status) => {
  switch (status) {
    case "warning":
      return <FiActivity className="alert-card-icon-svg" />;
    case "critical":
      return <FiAlertTriangle className="alert-card-icon-svg" />;
    case "success":
      return <FiCheckCircle className="alert-card-icon-svg" />;
    case "info":
    default:
      return <FiInfo className="alert-card-icon-svg" />;
  }
};

const NOTIFICATION_TITLES = {
  offlineDevice: "Device Offline",
  alarm: "Alarm Triggered",
  message: "System Message",
  critical: "Critical Alert",
  warning: "Warning Alert",
  success: "System Update",
  info: "Notification",
};

const getRedirectUrl = (item) => {
  if (item?.section === "offlineDevice") {
    return `/devices/details/${item?.redirectableId}`;
  } else if (item?.section === "alarm") {
    return `/alarm-history`;
  } else if (item?.section === "message") {
    return `/subscription/details/${item?.redirectableId}`;
  }
  return "";
};

const Header = ({ handle }) => {
  const ref = useRef();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { userDetails } = useSelector((state) => state.user);
  const business = userDetails?.business?.[0] || {};
  const { isDark, isNavOpen } = useSelector((state) => state.theme);

  const [appNotifications, setAppNotifications] = useState([]);
  const [unreadAppNotificationCount, setUnreadAppNotificationCount] = useState(0);
  const [notificationOpen, setNotificationOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [limit, setLimit] = useState(7);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const onMarkAll = async (payload) => {
    setIsLoading(true);
    try {
      await api.put(endpoint.readAppNotification, {
        id: payload,
      });
      getAppNotification();
      window.dispatchEvent(new CustomEvent("appNotificationRead"));
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
    setIsLoading(false);
  };

  const getAppNotification = async () => {
    ref.current = true;
    setIsLoading(true);
    try {
      const { results, unreadCount, count } = await api.get(endpoint.getAppNotifications, {
        params: {
          limit,
          page,
        },
      });
      setUnreadAppNotificationCount(unreadCount);
      setCount(count);
      setAppNotifications(results);
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
    setIsLoading(false);
    ref.current = false;
  };

  const toggleShow = () => {
    dispatch(changeNavOpen(!isNavOpen));
  };

  const directLogout = async () => {
    try {
      await axios.get(`/user/logout`, {
        withCredentials: true,
      });
      localStorage.setItem("isLoggedIn", "no");
      dispatch(setActiveDashboard(""));
      dispatch(setIsLogged(false));
      dispatch(changeTheme(false));
      dispatch(setUserDetails({}));
      // window.localStorage.clear();
      if (business?.businessUrl) {
        navigate("/" + business?.businessUrl);
      } else {
        navigate("/");
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error.message);
    }
  };
  const logOut = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to logout.",
      //icon: "warning",
      dangerMode: true,
      showCancelButton: true,
      confirmButtonText: "Confirm",
    }).then((result) => {
      if (result.isConfirmed) {
        directLogout();
      }
    });
  };

  useEffect(() => {
    getAppNotification();
  }, [limit, page]);

  // const handlePlay = () => {
  //   const audio = new Audio(notificationSound);
  //   audio.play().catch((error) => {
  //     // eslint-disable-next-line no-console
  //     console.error("Failed to play audio:", error);
  //   });
  // };

  useEffect(() => {
    socket.on("messageResponse", (data) => {
      // new app notification
      if (data?.key === "newAppNotification") {
        document.getElementById("playButton")?.click();
        getAppNotification();
        // new device login
      } else if (data?.key === "newLogin" && data?.activeDeviceId) {
        const activeDeviceId = localStorage.getItem("activeDeviceId");
        if (
          data?.activeDeviceId !== activeDeviceId &&
          userDetails?.role &&
          userDetails?._id === data?.userId &&
          userDetails?.role !== userRole.superAdmin
        ) {
          directLogout();
        }
      }
    });
  }, [socket, userDetails]);

  useEffect(() => {
    const handleSync = () => {
      getAppNotification();
    };
    window.addEventListener("appNotificationRead", handleSync);
    return () => {
      window.removeEventListener("appNotificationRead", handleSync);
    };
  }, []);

  const handleToggleTheme = async () => {
    const nextDark = !isDark;
    const themeName = nextDark ? "dark" : "light";
    dispatch(changeTheme(nextDark));
    dispatch(setUserDetails({ ...userDetails, theme: themeName }));
    try {
      await axios.put("/user/me", { theme: themeName }, { withCredentials: true });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to update theme in user model:", err);
    }
  };

  return (
    <div className="az-header py-2" style={{ height: "auto" }}>
      <div className="d-flex align-items-center">
        <div className="az-header-left d-block mr-3">
          <button
            id="azSidebarToggle"
            className="bg-transparent border-0 az-header-menu-icon me-3"
            onClick={() => toggleShow()}
          >
            <span></span>
          </button>
        </div>
        {location.pathname === "/" && <HomeSelect {...{ setIsLoading, className: "d-none d-sm-block" }} />}

        <div className="az-header-right ms-auto" style={{ userSelect: "none" }}>
          {location.pathname === "/" && (
            <div style={{ cursor: "pointer" }} onClick={handle.enter} className="px-1 py-2">
              <FiMaximize className="tx-16" />
            </div>
          )}
          <div
            style={{ cursor: "pointer" }}
            onClick={handleToggleTheme}
            className={`ps-3 py-2 ${userDetails?.role === userRole.installer && "me-3"}`}
          >
            {isDark ? <IoMoon className="tx-16" /> : <IoSunnySharp className="tx-16" />}
          </div>
          {userDetails?.role !== userRole.installer && (
            <div style={{ margin: "0px 23px 0px 10px", cursor: "pointer" }}>
              <Dropdown
                open={notificationOpen}
                onOpenChange={setNotificationOpen}
                trigger={["click"]}
                popupRender={() => (
                  <div className="alerts-dropdown-panel">
                    <div className="alerts-dropdown-header">
                      <p className={`alerts-dropdown-title mb-0 ${isDark ? "text-light" : "text-black"}`}>
                        Notifications
                      </p>
                      {appNotifications?.length > 0 && (
                        <Button
                          type="text"
                          danger
                          size="small"
                          className="alerts-mark-read-btn"
                          onClick={() => onMarkAll("all")}
                        >
                          Mark All As Read
                        </Button>
                      )}
                    </div>

                    <div className="alerts-list-container">
                      {!appNotifications?.length ? (
                        <div className="alerts-empty-state">
                          <p className="mb-0">There are no notifications for you.</p>
                        </div>
                      ) : (
                        appNotifications.map((item) => {
                          const status = getNotificationStatus(item?.status);
                          const isUnread = !item?.readers?.some((id) => id === userDetails?._id);
                          const title =
                            item?.title ||
                            NOTIFICATION_TITLES[item?.section] ||
                            NOTIFICATION_TITLES[status] ||
                            "Notification";
                          const subtitle = item?.subtitle || (item?.site?.name ? item.site.name : null);
                          const redirectUrl = getRedirectUrl(item);
                          const timeText = item?.createdAt ? dayjs(item.createdAt).format("hh:mm A") : "";

                          const cardContent = (
                            <>
                              <div className="alert-card-icon">{getStatusIcon(status)}</div>
                              <div className="alert-card-content">
                                <div className="alert-card-header-row">
                                  <span className="alert-card-title">{title}</span>
                                  <span className="alert-card-time">{timeText}</span>
                                </div>
                                {subtitle && <div className="alert-card-subtitle">{subtitle}</div>}
                                {item?.msg && (
                                  <div
                                    className="alert-card-message"
                                    dangerouslySetInnerHTML={{ __html: item.msg }}
                                  />
                                )}
                              </div>
                            </>
                          );

                          return redirectUrl ? (
                            <Link
                              key={item?._id}
                              to={redirectUrl}
                              className={`alert-card alert-card-${status} ${isUnread ? "alert-unread" : "alert-read"}`}
                              onClick={() => {
                                onMarkAll(item?._id);
                                setNotificationOpen(false);
                              }}
                            >
                              {cardContent}
                            </Link>
                          ) : (
                            <div
                              key={item?._id}
                              className={`alert-card alert-card-${status} ${isUnread ? "alert-unread" : "alert-read"}`}
                              onClick={() => onMarkAll(item?._id)}
                            >
                              {cardContent}
                            </div>
                          );
                        })
                      )}
                    </div>

                    <div className="alerts-dropdown-footer">
                      <Link
                        to="/alarm-history"
                        className="alerts-view-all-btn"
                        onClick={() => setNotificationOpen(false)}
                      >
                        View All Alerts
                      </Link>
                    </div>
                  </div>
                )}
              >
                <div style={{ cursor: "pointer" }}>
                  <Badge count={unreadAppNotificationCount} overflowCount={10}>
                    <IoMdNotificationsOutline style={{ fontSize: "22px" }} />
                  </Badge>
                </div>
              </Dropdown>
            </div>
          )}

          <Dropdown
            classNames={{ root: "profile-opt-dorpdown" }}
            menu={{
              items: [
                {
                  key: 1,
                  disabled: true,
                  label: (
                    <div className="az-header-profile">
                      <div className="az-img-user">
                        <img src={userDetails?.avatar} alt="" />
                      </div>
                      <h6>{userDetails?.name}</h6>
                      <span className="text-capitalize" style={{ color: "var(--primary_text_color)" }}>
                        {userRoleFormate[userDetails.role] || "Site User"}
                      </span>
                    </div>
                  ),
                },
                {
                  key: 2,
                  label: (
                    <Link to="/settings" className="dropdown-item">
                      Settings
                    </Link>
                  ),
                },
                {
                  key: 3,
                  label: (
                    <Link to="/profile" className="dropdown-item">
                      Profile
                    </Link>
                  ),
                },
                {
                  key: 4,
                  label: (
                    <button className="dropdown-item border-0 bg-transparent" onClick={() => logOut()}>
                      Logout
                    </button>
                  ),
                },
              ],
            }}
            trigger={["click"]}
          >
            <div className="az-img-user online" style={{ cursor: "pointer" }}>
              <img src={userDetails?.avatar} alt="user" />
            </div>
          </Dropdown>
        </div>
      </div>

      {location.pathname === "/" && <HomeSelect {...{ setIsLoading, className: "d-block d-sm-none mt-3" }} />}
    </div>
  );
};

export default Header;

Header.propTypes = {
  handle: PropTypes.object,
};
